import { Button, type ButtonVariant, Dialog, DialogCancel, Textarea } from '@fruuit/ui'
import { type FormEvent, type ReactNode, useEffect, useState } from 'react'

export interface ReasonDialogProps {
  open: boolean
  title: string
  description?: ReactNode
  confirmLabel: string
  variant?: ButtonVariant
  /** Ask for a reason of at least 8 characters (audited). Without it, a plain confirmation. */
  withReason?: boolean
  pending?: boolean
  error?: string | undefined
  onConfirm: (reason: string) => void
  onClose: () => void
}

/** Confirmation for consequential admin actions, optionally with a recorded reason. */
export function ReasonDialog({
  open,
  title,
  description,
  confirmLabel,
  variant = 'primary',
  withReason = false,
  pending = false,
  error,
  onConfirm,
  onClose,
}: ReasonDialogProps) {
  const [reason, setReason] = useState('')
  const [localError, setLocalError] = useState<string | undefined>()
  useEffect(() => {
    if (open) {
      setReason('')
      setLocalError(undefined)
    }
  }, [open])

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (withReason && reason.trim().length < 8) {
      setLocalError('Record a reason of at least 8 characters.')
      return
    }
    onConfirm(reason.trim())
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      title={title}
      description={description}
      footer={
        <>
          <DialogCancel />
          <Button type="submit" form="reason-dialog-form" variant={variant} loading={pending}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <form id="reason-dialog-form" onSubmit={onSubmit} className="flex flex-col gap-3">
        {withReason ? (
          <Textarea
            label="Reason"
            hint="Recorded on the platform audit log."
            name="reason"
            rows={3}
            required
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            error={localError}
          />
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}
      </form>
    </Dialog>
  )
}
